export type PlatformRole = 'administrator' | 'staff'

export type PlatformUser = {
  id: string
  username: string
  email: string
  first_name: string
  last_name: string
  role: PlatformRole
  capabilities: string[]
  mfa_enabled: boolean
  session_id: string
}

export type SessionResponse = {
  access: string
  token_type: 'Bearer'
  expires_in: number
  session_id: string
}

export type MfaChallenge = { mfa_required: true; challenge: string; expires_in: number }
export type Profile = { first_name: string; last_name: string; email: string }
export type Preferences = { language: 'pt-br' | 'en' | 'es'; timezone: string; show_help: boolean; show_notices: boolean }
export type MfaStatus = { enabled: boolean; recovery_codes_remaining: number }
export type MfaEnrollment = { secret: string; provisioning_uri: string; expires_in: number }
export type RecoveryCodes = { recovery_codes: string[]; reauthentication_required: true }
export type PlatformSession = { id: string; created_at: string; last_seen_at: string; expires_at: string; user_agent: string }
export type Sessions = { current_session_id: string; results: PlatformSession[] }

export type TeamMember = {
  id: number
  user_id: number
  username: string
  email: string
  name: string
  role: PlatformRole
  is_active: boolean
  created_at: string
  updated_at: string
}

export type Invitation = {
  id: number
  email: string
  role: PlatformRole
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
  created_at: string
}

export type Paginated<T> = { count: number; next: string | null; previous: string | null; results: T[] }
export type Account = {
  id: string; name: string; status: 'active' | 'suspended' | 'closed'; timezone: string
  billing_manager_id: number | null; is_in_trial: boolean; trial_ends_at: string; retention_cutoff_at: string | null; created_at: string
}
export type Membership = {
  id: string; account_id: string; account_name: string; user_id: number; username: string; email: string; name: string
  role: string; is_active: boolean; can_manage_billing: boolean
}
export type CustomerUser = {
  id: number; username: string; email: string; first_name: string; last_name: string; is_active: boolean; account_memberships: Membership[]
}
export type CompanySummary = { id: string; name: string; cnpj: string; is_active: boolean }
export type Plan = { id: string; code: string; name: string; sort_order: number; is_published: boolean; quote_only: boolean }
export type PlanVersion = {
  id: string; plan: Plan; version: number; status: 'draft' | 'published' | 'archived'; price: string | null; currency: string
  delivery_limit: number; company_limit: number; manager_limit: number; courier_limit: number; retention_months: number
  sefaz_import?: boolean | null; standard_support?: boolean | null; priority_support?: boolean | null; custom_sla?: boolean | null
  storage_limit_bytes: number | null; features: unknown; effective_at: string | null
}
export type Subscription = {
  id: string; account_id: string; account_name: string; modality: 'paid' | 'trial' | 'beta'; status: string; plan_version: PlanVersion
  pending_plan_version_id: string | null; cycle_start: string; cycle_end: string; current_period_start: string | null
  current_period_end: string | null; paid_through: string | null; next_due_date: string | null; collection_mode: string
  cancel_at_period_end: boolean; cancelled_at: string | null; renewal_disabled_at: string | null; renewal_state: string
  provider_configuration_id: string | null; external_subscription_id: string | null
}
export type BillingOperation = {
  id: string; account_id: string; subscription_id: string | null; order_id: string | null
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'unknown'; error_message: string
  correlation_id: string; request_id: string; idempotency_key: string
}
export type BillingCharge = {
  id: string; account_id: string; subscription_id: string | null; plan_version_id: string
  status: string; gross_cents: number; currency: string; raw_provider_status: string; last_checked_at: string | null
}
export type Usage = {
  id: string; subscription_id: string; starts_at: string; ends_at: string; deliveries_used: number; deliveries_reserved: number
  total: number; delivery_limit: number; storage_bytes: number
}
export type BetaBlocker = {
  code: string; detail?: string; dependency?: string; order_ids?: string[]; subscription_ids?: string[]
  quotas?: Record<string, { used: number; limit: number }>
}
export type BetaBenefit = {
  id: string; account_id: string; account_name: string; plan_version_id: string; effective_plan_version_id: string
  subscription_id: string; starts_at: string; ends_at: string | null; ended_at: string | null; state: string
  activation_blockers: BetaBlocker[]; reason: string; granted_by_id: number; created_at: string
}
export type AccountDetail = Account & {
  billing_manager: CustomerUser | null; memberships: Membership[]; companies: CompanySummary[]; subscription: Subscription | null
  usage: Usage | null; beta: BetaBenefit | null; history_url: string; subscriptions_url: string; usage_url: string
  retention_url: string; trial_url: string
}
export type AccountHistory = { id: string; actor_id: number | null; command: string; object_type: string; object_id: string; reason: string; created_at: string }
export type BetaEligibility = { eligible: boolean; blockers: BetaBlocker[] }
export type Trial = { account_id: string; is_in_trial: boolean; ends_at: string; expired: boolean; invoices_used: number; invoice_limit: number; usage_url: string }
export type Retention = {
  account_id: string; policy_months: number | null; stored_cutoff_at: string | null; effective_cutoff_at: string | null
  extra_contracts: Record<string, unknown>[]; dependencies: Record<string, unknown>; destructive_action_available: boolean
}
export type MoneyMetric = {
  amounts: Record<string, number>; complete: boolean; source: string; definition: string; excluded_undefined_price?: number
  excluded_missing_amount?: number; excluded_missing_occurred_at?: number; payment_count?: number; refund_count?: number
  charge_count?: number; source_last_observed_at?: string | null; source_provider_updated_at?: string | null
  source_freshness?: { provider_configuration_id: string; provider_code: string; environment: string; status: string; last_observed_at: string | null; provider_updated_at: string | null }[]
  expected_source_count?: number; stale_source_count?: number; never_observed_source_count?: number; freshness_threshold_minutes?: number
}
export type DashboardBase = {
  scope: PlatformRole; accounts: { total: number; by_access_status: Record<string, number> }
  attention_queue: { code: string; count: number; destination: string }[]
  freshness: { generated_at: string; period_start: string; period_end: string; period_end_exclusive: boolean }
}
export type AdminDashboard = DashboardBase & {
  scope: 'administrator'; subscriptions: { total: number; paying_active: number; by_status: Record<string, number>; by_modality: Record<string, number> }
  beta: Record<string, number>; trials: Record<string, number>; usage: { deliveries: number; adopting_accounts: number; source: string }
  financial: { mrr: MoneyMetric; received: MoneyMetric; overdue: MoneyMetric }; series: null; limitations: string[]
}
export type StaffDashboard = DashboardBase & { scope: 'staff'; support: Record<string, number> }
export type Dashboard = AdminDashboard | StaffDashboard
export type AuditEvent = {
  id: string; source: 'account' | 'platform' | 'support_event' | 'support_transition'; actor_id: number | null; actor: string | null
  account_id: string | null; command: string; target_type: string; target_id: string; reason: string; before: unknown; after: unknown
  request_id: string; correlation_id: string; created_at: string
}
export type Contracts = { base_path: string; screens: { screen: string; capability: string; endpoints: string[] }[]; unavailable_capabilities: { capability: string; reason: string }[] }
export type PlatformNotification = {
  id: string
  notification_type: string
  subject: string
  message: string
  created_at: string
  read_at: string | null
  is_read: boolean
  destination: string
}
export type Notifications = Paginated<PlatformNotification> & { delivery_policy?: Record<string, unknown> }
export type About = { application: string; version: string; environment: string }
export type LegalDocument = { id: string; version: number; text: string; published_at?: string }

export type ContactRequest = {
  id: string
  name: string
  email: string
  phone: string
  subject: string
  contact_type: string
  message: string
  proposal: null | {
    id: string
    proposal_type: 'custom_plan' | 'addon'
    status: string
    plan: string | null
    additional_service: string | null
    account: string | null
    has_active_plan: boolean | null
    delivery_volume: number | null
    retention_months: number | null
    company_count: number | null
    quantity: number
    currency: string
    unit_price: string | number | null
    monthly_price: string | number | null
    total_setup_price: string | number | null
    notes: string
  }
  consent: boolean
  status: string
  is_read: boolean
  read_at: string | null
  read_by: number | string | null
  source: string
  page_url: string
  referrer: string
  user_agent: string
  ip_address: string | null
  created_at: string
  updated_at: string
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message)
  }
}

export function roleLabel(role: PlatformRole) {
  return role === 'administrator' ? 'Administrador' : 'Staff'
}
